import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ShieldCheck, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { API, formatApiError } from "@/lib/shared";

export default function ConsentSignPage() {
  const { patientId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [hipaa, setHipaa] = useState(false);
  const [consent, setConsent] = useState(false);
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("self");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/consent/${patientId}`, { params: { token } })
      .then(r => setData(r.data))
      .catch(e => setError(formatApiError(e.response?.data?.detail) || "This link is invalid or has expired."));
  }, [patientId, token]);

  const submit = async () => {
    if (!hipaa || !consent) { toast.error("Please check both acknowledgments."); return; }
    if (name.trim().length < 2) { toast.error("Type your full legal name as your signature."); return; }
    setSubmitting(true);
    try {
      const r = await axios.put(`${API}/public/consent/${patientId}`, { hipaa_acknowledged: true, treatment_consent: true, signature_name: name.trim(), signer_relationship: relationship }, { params: { token } });
      setDone(r.data.signed_at);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save signature"); }
    finally { setSubmitting(false); }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-[#070707] text-foreground">
      <Toaster richColors position="top-center" />
      <div className="max-w-3xl mx-auto px-5 py-10">
        <div className="text-center mb-8">
          <img src="/eliteiv-logo.png" alt="EliteIV Lounge" className="w-28 h-28 object-contain mx-auto" />
          <p className="text-[11px] tracking-[0.35em] uppercase text-primary/80 mt-2">Patient Privacy Notice & Consent</p>
        </div>
        {children}
      </div>
    </div>
  );

  if (error) return shell(<div className="rounded-xl border border-rose-500/40 bg-rose-950/30 p-6 text-center" data-testid="consent-error"><p className="text-rose-200">{error}</p><p className="text-xs text-muted-foreground mt-2">Please call or text us at 727-503-2572 for a new link.</p></div>);
  if (!data) return shell(<p className="text-center text-muted-foreground">Loading…</p>);
  if (done) return shell(
    <div className="rounded-xl border-2 border-emerald-500/50 bg-emerald-950/20 p-8 text-center" data-testid="consent-success">
      <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
      <h2 className="text-2xl font-bold brand-heading mt-3">Thank you, {data.patient.first_name}!</h2>
      <p className="text-sm text-muted-foreground mt-2">Your HIPAA acknowledgment and Consent for Treatment were signed on {new Date(done).toLocaleString()} and saved to your chart. We look forward to seeing you.</p>
    </div>
  );

  const d = data.document;
  return shell(
    <div className="space-y-6" data-testid="consent-page">
      <div className="rounded-xl border border-primary/40 bg-black/40 p-6">
        <h1 className="text-2xl md:text-3xl font-extrabold brand-heading gold-gradient-text">{d.title}</h1>
        <p className="text-xs text-muted-foreground mt-1">Effective {d.effective_date} · Prepared for <span className="text-primary font-medium">{data.patient.first_name} {data.patient.last_name}</span></p>
        {data.last_signature && <p className="text-xs text-emerald-300 mt-2" data-testid="consent-already-signed">Previously signed {new Date(data.last_signature.signed_at).toLocaleDateString()} by {data.last_signature.signature_name}. Signing again will update your chart.</p>}
      </div>

      <div className="space-y-3">
        {d.sections.map(([title, body], i) => (
          <section key={i} className="rounded-xl border-2 border-primary/50 bg-black/30 p-5" data-testid={`consent-section-${i}`}>
            <h3 className="text-lg font-extrabold uppercase tracking-wide text-primary flex items-center gap-2">
              {/consent/i.test(title) ? <PenLine className="w-4 h-4" /> : /rights/i.test(title) ? <ShieldCheck className="w-4 h-4" /> : <span className="inline-flex w-6 h-6 items-center justify-center rounded border border-primary/60 text-xs">{i + 1}</span>}
              {title}
            </h3>
            <p className="text-sm leading-relaxed text-foreground/90 mt-2">{body}</p>
          </section>
        ))}
      </div>

      <div className="rounded-xl border-2 border-primary bg-primary/10 p-6 space-y-5" data-testid="consent-sign-box">
        <p className="text-lg font-extrabold uppercase tracking-wide text-primary">Patient Acknowledgment & Electronic Signature</p>
        <p className="text-sm text-muted-foreground">{d.patient_acknowledgment}</p>
        <label className="flex items-start gap-3 text-sm cursor-pointer">
          <Checkbox checked={hipaa} onCheckedChange={v => setHipaa(!!v)} className="mt-0.5" data-testid="consent-hipaa-check" />
          <span><strong className="text-primary">HIPAA Privacy Notice.</strong> I have received and read EliteIV Lounge's Notice of Privacy Practices and understand my Patient Rights.</span>
        </label>
        <label className="flex items-start gap-3 text-sm cursor-pointer">
          <Checkbox checked={consent} onCheckedChange={v => setConsent(!!v)} className="mt-0.5" data-testid="consent-treatment-check" />
          <span><strong className="text-primary">Consent for Treatment.</strong> I have read the Consent for Treatment section, including the risk of infection and other risks listed, and I voluntarily consent to IV/IM therapy.</span>
        </label>
        <div className="grid sm:grid-cols-[1fr_200px] gap-4">
          <div className="space-y-1.5">
            <Label>Type your full legal name as your signature</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder={`${data.patient.first_name} ${data.patient.last_name}`} className="font-serif italic text-lg h-12" data-testid="consent-signature-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Signing as</Label>
            <select value={relationship} onChange={e => setRelationship(e.target.value)} className="h-12 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="consent-relationship">
              <option value="self">Myself (patient)</option>
              <option value="parent_guardian">Parent / legal guardian</option>
              <option value="poa">Power of attorney</option>
            </select>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">By clicking Sign, you agree that your typed name is your legal electronic signature. Date, time, and device information are recorded with your signature.</p>
        <Button onClick={submit} disabled={submitting || !hipaa || !consent} className="w-full h-12 bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 text-black font-semibold tracking-widest" data-testid="consent-sign-submit">
          {submitting ? "Saving…" : "Sign & Submit"}
        </Button>
      </div>
    </div>
  );
}
